import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';

import type { GenerationPhase } from '../../model/generationProgress';
import { GenerationProgressBar } from '../GenerationProgressBar';
import { LoadingSpinner } from '../LoadingSpinner';
import { WaitingSurveyCarousel } from '../Survey';

/**
 * Everything the project page shows before the score is ready: a blank page
 * with the spinner or progress bar in the vertical middle and the waiting
 * surveys underneath. The equal spacers above and below keep the indicator
 * centered whether or not a survey is showing.
 */
export const GenerationWaitingView: React.FC<{
  status: string;
  phase: GenerationPhase | null;
  awaitingHandSelection: boolean;
  isPolling: boolean;
  statusMessage: string;
  hasError: boolean;
  answeredWaitingSurveyTypes: string[] | null;
  /** Where the failure button goes. Members return to the workspace. */
  backTo?: string;
  backLabel?: string;
  onBack?: () => void;
}> = ({
  status,
  phase,
  awaitingHandSelection,
  isPolling,
  statusMessage,
  hasError,
  answeredWaitingSurveyTypes,
  backTo = '/workspace',
  backLabel = 'My Project',
  onBack,
}) => {
  const generating = status === 'PENDING_GENERATION';
  // Awaiting hand selection looks like polling but nothing is running, so it
  // gets no indicator and no surveys; the dialog is what the user should look at.
  const showSurveys =
    !awaitingHandSelection &&
    (generating || status === 'POLLING_STARTED' || isPolling);

  return (
    <div
      className={`
        flex min-h-[calc(100vh-8rem)] w-full flex-col items-center px-4
      `}
    >
      <div className="flex-1" />
      <div className="flex w-full flex-col items-center gap-4 text-center">
        {!awaitingHandSelection &&
          (generating || status === 'GENERATION_FAILED') && (
            <GenerationProgressBar status={status} phase={phase} />
          )}
        {!awaitingHandSelection && status === 'POLLING_STARTED' && (
          <LoadingSpinner variant="prominent" color="yellow" />
        )}
        {statusMessage && (
          <div className="text-md text-gray-800">{statusMessage}</div>
        )}
        {status === 'GENERATION_FAILED' && (
          <Link
            to={backTo}
            onClick={() => onBack?.()}
            className={`
              inline-flex items-center gap-x-2 rounded-full border
              border-gray-700 bg-white px-5 py-2 text-sm font-medium
              text-gray-700 transition-colors
              hover:bg-gray-50
            `}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
            <span>{backLabel}</span>
          </Link>
        )}
        {hasError && (
          <div className="text-sm text-red-600">
            An error occurred. Please try refreshing the page.
          </div>
        )}
      </div>
      <div className="flex w-full flex-1 flex-col items-center pt-8">
        {showSurveys && (
          <WaitingSurveyCarousel
            answeredSurveyTypes={answeredWaitingSurveyTypes}
            isGenerating={isPolling || generating}
          />
        )}
      </div>
    </div>
  );
};
